import {
  CopyMode,
  MoldOrderType,
  MoldOrderFirstLevelType,
  OrderDetail,
  ShiftType,
} from '../../types/mold';
import { getBangkokDateWithOffset } from '../../utils/dateUtils';

/**
 * Abstract base class for mold order models
 * Defines the common interface and behavior for all mold order types
 */
export abstract class BaseMoldOrderModel {
  /**
   * Create initial order detail for a specific mold order type (subType)
   * Default: just call createInitialOrderDetail
   */
  createInitialOrderDetailForType(
    moldOrderType: string,
    isSubOrder: boolean,
  ): OrderDetail {
    return this.createInitialOrderDetail();
  }
  abstract readonly type: MoldOrderFirstLevelType;
  abstract readonly label: string;
  abstract readonly description: string;
  abstract readonly iconColor: string;
  abstract readonly headerBgColor: string;
  abstract readonly headerBorderColor: string;
  abstract readonly headerShadow: string;
  abstract readonly headerTextColor: string;

  /**
   * Get the default mold order type for this model
   */
  abstract getDefaultMoldOrderType(): MoldOrderType;

  /**
   * Get available mold order types for this model
   */
  abstract getAvailableMoldOrderTypes(): MoldOrderType[];

  /**
   * Create initial order detail with default values
   */
  abstract createInitialOrderDetail(): OrderDetail;

  /**
   * Validate if the order detail is complete and valid
   */
  abstract validateOrderDetail(orderDetail: OrderDetail): {
    isValid: boolean;
    errors: string[];
  };

  /**
   * Get required form fields for this order type
   */
  abstract getRequiredFormFields(): string[];

  /**
   * Check if storage location is required
   */
  abstract requiresStorageLocation(moldOrderType: MoldOrderType): boolean;

  /**
   * Check if shift completion info is required
   */
  abstract requiresShiftCompletedInfo(moldOrderType: MoldOrderType): boolean;

  /**
   * Check if machine to produce is required
   */
  abstract requiresMachineToProduce(moldOrderType: MoldOrderType): boolean;

  /**
   * Get default shift type for this order
   */
  getDefaultShiftType(): ShiftType {
    return 'A';
  }

  /**
   * Get default due date (today + offset days)
   */
  getDefaultDueDate(offsetDays: number = 0): Date {
    return getBangkokDateWithOffset(offsetDays);
  }

  /**
   * Transform order detail from previous order (for creating follow-up orders)
   */
  transformFromPreviousOrder(
    previousOrder: OrderDetail,
    copyMode: CopyMode = 'none',
    carriedForm: Record<string, any> = {},
    targetType?: MoldOrderType,
    carryKeys: string[] = [],
  ): OrderDetail {
    // Preserve the source's EXACT subtype when the caller supplies it; never silently
    // fall back to the model default (that would open a repair.MAJOR follow-up as the
    // default repair subtype).
    const initial = targetType
      ? this.createInitialOrderDetailForType(targetType, false)
      : this.createInitialOrderDetail();
    // "กรอกใหม่ทั้งหมด" → a truly blank order.
    if (copyMode === 'none') return initial;
    // 'metadata' / 'all' → carry the order-CREATION inputs verbatim. `carryKeys` are the
    // target type's create-form field keys (resolved from the runtime form schema by the
    // caller), so we copy exactly the fields the create wizard collects for this
    // type/company — nothing else leaks. `moldOrderType` and `form` are handled explicitly.
    //
    // The questionnaire `form` (+ its photos) is WORKFLOW data. It is carried only for 'all'
    // (already version-diffed by the caller); for 'metadata' it starts blank so it is
    // re-filled for the new job (the previous order may have failed / been rejected, so its
    // answers must not be silently reused).
    const carried: Record<string, unknown> = {};
    const source = previousOrder as unknown as Record<string, unknown>;
    for (const key of carryKeys) {
      if (key === 'moldOrderType' || key === 'form') continue;
      if (source?.[key] !== undefined) carried[key] = source[key];
    }
    return {
      ...initial,
      ...carried,
      moldOrderType: targetType ?? this.getDefaultMoldOrderType(),
      form: copyMode === 'all' ? carriedForm : {},
    };
  }

  /**
   * Get step validation rules for multi-step forms
   */
  getStepValidationRules(): Record<
    number,
    (orderDetail: OrderDetail) => boolean
  > {
    return {
      1: () => true, // Mold selection step
      2: (orderDetail: OrderDetail) =>
        this.validateOrderDetail(orderDetail).isValid,
      3: () => true, // Review step
    };
  }
}
